/**
 * Orchestrator Agent - Context Note Section
 * 
 * Section 4: Context Engineering Note
 * 
 * Explains how context IDs work, how to reference artifacts,
 * and how dynamic content flows through the system.
 * 
 * Keywords AI Key: ORCHESTRATOR_CONTEXT_ADDITIONAL
 */

import { PromptSectionBase } from '../../base/systemPrompt/PromptSectionBase';
import { buildContextEngineeringNote, CONTEXT_ID_PREFIXES } from '../../base/systemPrompt/shared';
import { ORCHESTRATOR_PROMPT_KEYS } from './variables';

/**
 * Context prefixes relevant to the orchestrator
 * (All prefixes since orchestrator coordinates all agents)
 */
export const ORCHESTRATOR_CONTEXT_PREFIXES: (keyof typeof CONTEXT_ID_PREFIXES)[] = [
    'alphasense',
    'externalFile', 
    'excel',
    'excelRead',
    'logo',
    'wikimedia',
    'asset',
    'artifact',
    'slide',
    'component',
    'shape',
];

class OrchestratorContextNoteSection extends PromptSectionBase {
    protected readonly keywordsKey = ORCHESTRATOR_PROMPT_KEYS.contextNote;
    protected readonly fallbackContent = `## Context Engineering

Context items are passed with specific IDs that you must use correctly to link dynamic content.

### Context ID Prefixes

- **as_xxx**: AlphaSense research results (earnings, filings, broker reports)
- **page_xxx**: External file content (PDFs, Word docs, uploaded files)
- **excel_xxx**: Excel workbook data and structure
- **excelread_xxx**: Excel read results with cell values and formulas
- **logo_xxx**: Brand logos from Brandfetch (company logos)
- **wikimedia_xxx**: Wikimedia images (stock photos, diagrams)
- **asset_xxx**: Presentation assets (embedded images, media)
- **artifact_xxx**: Generated artifacts (charts, tables for insertion)
- **slide_xxx**: Slide data from readSlide (shapes, layout, content)
- **component_xxx**: Component search results from presentations
- **shape_xxx**: Individual shape references from slides

### Using Context IDs

1. **Reference by ID**: When passing context to sub-agents, use the exact ID
   \`\`\`json
   { "context": ["as_001", "logo_acme"], "purpose": "Research and branding for slide" }
   \`\`\`

2. **Include purpose**: Always explain what the context is for

3. **Group related context**: Put related items in the same context array

4. **Don't fabricate IDs**: Only use IDs returned from tools/searches

### Passing Context to Sub-Agents

When delegating tasks to sub-agents, pass relevant context:

\`\`\`json
{
  "mode": "quick",
  "instruction": "Insert the chart using the provided data",
  "targetSlide": 3,
  "context": [
    { "context": ["as_001", "as_002"], "purpose": "Research data for the chart" },
    { "context": ["logo_acme"], "purpose": "Company logo for branding" }
  ]
}
\`\`\`

### Context Lifetime

- Context IDs are valid for the duration of the conversation
- IDs from tools like readSlide (slide_xxx) persist until slide changes
- Research results (as_xxx, page_xxx) persist indefinitely
- Artifact IDs (artifact_xxx) are created fresh each time

### Common Context Patterns

1. **Research → Edit flow**:
   - Call alphaSenseSearchAgent → get as_xxx IDs
   - Pass as_xxx to writeSlide context

2. **Read → Edit flow**:
   - Call readSlide → get slide_xxx ID
   - Pass slide_xxx to writeSlide context

3. **Search → Insert flow**:
   - Call imageSearchAgent with detailed requirements including:
     - Background preference (e.g., "for dark background")
     - Aspect ratio (e.g., "square shape", "horizontal shape")
     - Placement context (e.g., "for header", "footer", "slide corner")
     - Example: "Microsoft logo for dark background, square shape for header"
   - **CRITICAL:** Extract asset IDs from results structure:
     - Results format: { results: [{ relevantResults: { assets: [{ assetId: "logo_omxqz4pl", ... }] } }] }
     - Extract: results[].relevantResults.assets[].assetId → ["logo_omxqz4pl", ...]
   - **ALWAYS use these EXACT asset IDs** (e.g., "logo_omxqz4pl"), NOT filenames or placeholder IDs
   - **MANDATORY:** Pass ALL logo asset IDs in GLOBAL context (params.context)
   - Example global context format:
     \`\`\`json
     {
       "targetSlideRef": "slide_325",
       "changes": [/* writeSlide operations */],
       "context": [
         { "context": ["logo_omxqz4pl"], "purpose": "Microsoft logo to add" },
         { "context": ["slide_325"], "purpose": "Current slide content" }
       ]
     }
     \`\`\`
   - **Why global context?** Only global context is resolved by the base class, so images must be there.
   - When writeSlide operations require image/context references, they use these exact asset IDs from global context`;
}

const contextNoteSection = new OrchestratorContextNoteSection();

/**
 * Get the context note section.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getContextNoteSection(useFallback?: boolean): string {
    return useFallback ? contextNoteSection.getFallbackContent() : contextNoteSection.getContent();
}

/**
 * Export constant for backwards compatibility
 */
export const ORCHESTRATOR_CONTEXT_NOTE = contextNoteSection.getFallbackContent();
